import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Trash2, Video, Mic, X, Pencil, Check, Download, BarChart2, Trophy, Ban, RefreshCw, Swords, Clock, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { RecordingService, Recording } from '../services/RecordingService';
import { JocelynScore } from '../pages/JocelynScore';
import { AudioWaveform } from './AudioWaveform';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ReactPlayer from 'react-player';
import { SyncCalculationService } from '../services/SyncCalculationService';
import WaveSurfer from 'wavesurfer.js';

interface RecordingsListProps {
  onPlay?: (recording: Recording) => void;
}

interface ScoringInfo {
  scoringStatus: string | null;
  scoringAttempts: number | null;
  scoringLastAttemptAt: string | null;
}

type StageTone = 'processing' | 'scoring' | 'ready' | 'failed';

interface StageDisplay {
  label: string;
  tone: StageTone;
  spinning: boolean;
  hint?: string;
}

const TONE_PILL_CLASSES: Record<StageTone, string> = {
  processing: 'text-amber-300 bg-amber-500/15 border-amber-500/30',
  scoring: 'text-blue-300 bg-blue-500/15 border-blue-500/30',
  ready: 'text-green-300 bg-green-500/15 border-green-500/30',
  failed: 'text-red-300 bg-red-500/15 border-red-500/30',
};

interface PlaybackModalProps {
  recording: Recording;
  onClose: () => void;
  onSyncCalculated?: (offset: number) => void;
  isScored?: boolean;
}

export const PlaybackModal: React.FC<PlaybackModalProps> = ({ recording, onClose, onSyncCalculated, isScored = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const backingTrackRef = useRef<HTMLAudioElement>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCalculatingSync, setIsCalculatingSync] = useState(false);
  const syncOffsetRef = useRef<number>(0);

  const hasFinalVideo = !!recording.final_video_url;
  const videoSource = recording.final_video_url || recording.recording_url;

  useEffect(() => {
    const calculateSyncIfNeeded = async () => {
      if (hasFinalVideo) return;
      if (recording.audio_sync_offset !== null && recording.audio_sync_offset !== undefined) {
        syncOffsetRef.current = recording.audio_sync_offset;
        return;
      }
      if (!recording.song?.audio_url) return;

      try {
        setIsCalculatingSync(true);
        console.log('Auto-calculating missing sync offset...');

        const offset = await SyncCalculationService.calculateAndSaveSyncOffset(recording);

        if (offset !== null) {
          syncOffsetRef.current = offset;
          console.log(`Sync offset calculated and saved: ${offset.toFixed(3)} seconds`);

          if (onSyncCalculated) {
            onSyncCalculated(offset);
          }
        }
      } catch (error) {
        console.error('Failed to calculate sync offset:', error);
        syncOffsetRef.current = 0;
      } finally {
        setIsCalculatingSync(false);
      }
    };

    calculateSyncIfNeeded();
  }, [recording.id, recording.audio_sync_offset, recording.song?.audio_url, hasFinalVideo, onSyncCalculated]);

  useEffect(() => {
    if (hasFinalVideo) return;
    if (isCalculatingSync) return;

    const video = videoRef.current;
    const audio = backingTrackRef.current;

    if (!video || !audio || !recording.song?.audio_url) return;

    video.volume = 1.0;
    audio.volume = 0.25;

    console.log(`Using audio sync offset: ${syncOffsetRef.current.toFixed(3)} seconds`);

    const syncAudio = () => {
      if (isSyncing) return;
      setIsSyncing(true);

      // Calculate expected audio position based on video time and sync offset
      const expectedAudioTime = video.currentTime - syncOffsetRef.current;
      const actualAudioTime = audio.currentTime;
      const timeDiff = Math.abs(expectedAudioTime - actualAudioTime);

      // Only adjust if drift is significant (> 0.1s)
      if (timeDiff > 0.1) {
        audio.currentTime = Math.max(0, expectedAudioTime);
      }

      setIsSyncing(false);
    };

    const handlePlay = () => {
      // Apply sync offset when starting playback
      const expectedAudioTime = video.currentTime - syncOffsetRef.current;
      audio.currentTime = Math.max(0, expectedAudioTime);
      audio.play().catch(err => console.error('Error playing backing track:', err));
    };

    const handlePause = () => {
      audio.pause();
    };

    const handleSeeking = () => {
      audio.pause();
    };

    const handleSeeked = () => {
      // Apply sync offset when seeking
      const expectedAudioTime = video.currentTime - syncOffsetRef.current;
      audio.currentTime = Math.max(0, expectedAudioTime);
      if (!video.paused) {
        audio.play().catch(err => console.error('Error playing backing track:', err));
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);

    // Periodic sync check as safety net (reduced frequency since we have offset)
    const syncInterval = setInterval(syncAudio, 2000);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
      clearInterval(syncInterval);
    };
  }, [recording.song?.audio_url, recording.audio_sync_offset, isSyncing, hasFinalVideo, isCalculatingSync]);

  return (
    <div className="fixed inset-0 bg-black/95 flex flex-col z-50">
      <div className="flex items-center justify-between p-4 border-b border-kod-purple-600/50 bg-kod-purple-900/50 backdrop-blur-sm">
        <div>
          <h3 className="font-semibold text-lg">{recording.song?.title || recording.title}</h3>
          <p className="text-sm text-white/60">
            {recording.song?.artist}
          </p>
          {isCalculatingSync && (
            <div className="flex items-center gap-2 mt-2">
              <RefreshCw size={14} className="animate-spin text-blue-400" />
              <span className="text-xs text-blue-400">Calculating audio sync...</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="p-2 hover:bg-kod-purple-700 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-black overflow-hidden">
        <div className="w-full h-full flex items-center justify-center">
          <video
            ref={videoRef}
            src={videoSource}
            className="w-full h-full object-contain"
            controls
            playsInline
            preload="auto"
            autoPlay
            crossOrigin="anonymous"
          />
          {!hasFinalVideo && recording.song?.audio_url && (
            <audio
              ref={backingTrackRef}
              src={recording.song.audio_url}
              preload="auto"
              style={{ display: 'none' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const RecordingsList: React.FC<RecordingsListProps> = ({ onPlay }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playingAudio, setPlayingAudio] = useState<Recording | null>(null);
  const [playingVideo, setPlayingVideo] = useState<Recording | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  const [isScoring, setIsScoring] = useState<string | null>(null);
  const [scoreStatus, setScoreStatus] = useState<{ id: string; status: string; error?: string } | null>(null);
  const [showJocelynModal, setShowJocelynModal] = useState<boolean>(false);
  const [recordingForScoring, setRecordingForScoring] = useState<Recording | null>(null);
  const [scoredRecordings, setScoredRecordings] = useState<Set<string>>(new Set());
  const [recordingScores, setRecordingScores] = useState<Map<string, number>>(new Map());
  const [scoringInfo, setScoringInfo] = useState<Map<string, ScoringInfo>>(new Map());
  const [submittingToLeaderboard, setSubmittingToLeaderboard] = useState<string | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const fxConnectedRef = useRef(false);

  const recordingService = new RecordingService();

  const recordingsRef = useRef<Recording[]>([]);
  useEffect(() => {
    recordingsRef.current = recordings;
  }, [recordings]);

  const scoringInfoRef = useRef<Map<string, ScoringInfo>>(scoringInfo);
  useEffect(() => {
    scoringInfoRef.current = scoringInfo;
  }, [scoringInfo]);

  useEffect(() => {
    loadRecordings();
  }, []);

  // Returns the download readiness of a recording's server-mixed media.
  const getDownloadStatus = (recording: Recording): 'ready' | 'processing' | 'failed' => {
    const isVideo = recording.recording_type === 'video';
    const finishedUrl = isVideo ? recording.final_video_url : recording.mixed_recording_url;
    if (finishedUrl) return 'ready';

    const failed =
      recording.processing_status === 'failed' ||
      recording.preprocessing_status === 'failed' ||
      recording.mix_status === 'failed' ||
      (isVideo && recording.video_processing_status === 'failed');
    if (failed) return 'failed';

    return 'processing';
  };

  // Friendly, kiosk-style status for a recording across media-processing and scoring.
  const getStageDisplay = (recording: Recording): StageDisplay => {
    const media = getDownloadStatus(recording);
    const ageMin = (Date.now() - new Date(recording.created_at).getTime()) / 60000;

    if (media === 'failed') {
      return {
        label: 'Processing failed',
        tone: 'failed',
        spinning: false,
        hint: 'We could not finish processing this take. Please record it again.',
      };
    }

    if (media === 'processing') {
      const isVideo = recording.recording_type === 'video';
      let label = 'Processing';
      if (recording.preprocessing_status === 'processing') label = 'Preparing audio';
      else if (recording.mix_status === 'mixing') label = 'Mixing audio';
      else if (isVideo && recording.video_processing_status === 'processing') label = 'Rendering video';
      const slow = ageMin > 5;
      return {
        label: slow ? 'Still working' : label,
        tone: 'processing',
        spinning: true,
        hint: slow
          ? 'This is taking a little longer than usual. Hang tight, we are still finishing your take.'
          : 'We are polishing your recording. This usually takes about a minute.',
      };
    }

    const sc = scoringInfo.get(recording.id);
    if (sc) {
      if (sc.scoringStatus === 'failed') {
        return {
          label: 'Scoring failed',
          tone: 'failed',
          spinning: false,
          hint: 'We could not score this take. Tap the score button to try again.',
        };
      }
      if (sc.scoringStatus === 'processing' || sc.scoringStatus === 'not_started') {
        const lastAt = sc.scoringLastAttemptAt
          ? new Date(sc.scoringLastAttemptAt).getTime()
          : new Date(recording.created_at).getTime();
        const slow = (Date.now() - lastAt) / 60000 > 3;
        return {
          label: slow ? 'Scoring delayed' : 'Scoring',
          tone: 'scoring',
          spinning: !slow,
          hint: slow
            ? 'Scoring is taking longer than expected. Your take was received, check back shortly.'
            : 'Our AI vocal coach is scoring your take. This usually takes under a minute.',
        };
      }
    }

    return { label: 'Ready', tone: 'ready', spinning: false };
  };

  // Only poll recordings that are actively being processed or were created recently,
  // so we never poll forever on legacy recordings that predate the mixing pipeline.
  const shouldPoll = (recording: Recording): boolean => {
    if (getDownloadStatus(recording) !== 'processing') return false;
    const isVideo = recording.recording_type === 'video';
    const inFlight =
      recording.processing_status === 'processing' ||
      recording.preprocessing_status === 'processing' ||
      recording.mix_status === 'mixing' ||
      (isVideo && recording.video_processing_status === 'processing');
    const ageMs = Date.now() - new Date(recording.created_at).getTime();
    const recentlyCreated = ageMs < 10 * 60 * 1000;
    return inFlight || recentlyCreated;
  };

  useEffect(() => {
    const interval = setInterval(async () => {
      const pollIds = recordingsRef.current.filter(shouldPoll).map((r) => r.id);
      if (pollIds.length === 0) return;

      const { data, error: pollError } = await supabase
        .from('recordings')
        .select('id, mixed_recording_url, final_video_url, mix_status, video_processing_status, processing_status, preprocessing_status')
        .in('id', pollIds);

      if (pollError || !data) return;

      setRecordings((prev) =>
        prev.map((r) => {
          const updated = data.find((d) => d.id === r.id);
          return updated ? { ...r, ...updated } : r;
        })
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Refresh scoring status for recordings that are still being scored or were created
  // recently (a score may land shortly after the media finishes processing).
  useEffect(() => {
    const interval = setInterval(async () => {
      const scoringIds = recordingsRef.current
        .filter((r) => {
          const sc = scoringInfoRef.current.get(r.id);
          if (sc && (sc.scoringStatus === 'processing' || sc.scoringStatus === 'not_started')) {
            return true;
          }
          const ageMs = Date.now() - new Date(r.created_at).getTime();
          return ageMs < 15 * 60 * 1000;
        })
        .map((r) => r.id);

      if (scoringIds.length === 0) return;

      try {
        const infos = await recordingService.getRecordingScoreInfo(scoringIds);
        if (infos.length === 0) return;

        setScoringInfo((prev) => {
          const next = new Map(prev);
          infos.forEach((i) =>
            next.set(i.recordingId, {
              scoringStatus: i.scoringStatus,
              scoringAttempts: i.scoringAttempts,
              scoringLastAttemptAt: i.scoringLastAttemptAt,
            })
          );
          return next;
        });
        setRecordingScores((prev) => {
          const next = new Map(prev);
          infos.forEach((i) => {
            if (i.score && i.score > 0) next.set(i.recordingId, i.score);
          });
          return next;
        });
        setScoredRecordings((prev) => {
          const next = new Set(prev);
          infos.forEach((i) => next.add(i.recordingId));
          return next;
        });
      } catch (err) {
        console.error('Scoring status poll failed:', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadRecordings = async () => {
    try {
      setLoading(true);
      if (!user?.id) {
        // If not logged in, just show empty state
        setRecordings([]);
        setLoading(false);
        return;
      }
      const data = await recordingService.getUserRecordings(user.id);
      setRecordings(data);

      // Check which recordings already have performances and fetch their scores + scoring status
      if (data.length > 0) {
        const infos = await recordingService.getRecordingScoreInfo(data.map((r) => r.id));

        if (infos.length > 0) {
          setScoredRecordings(new Set(infos.map((i) => i.recordingId)));

          const scoresMap = new Map<string, number>();
          const siMap = new Map<string, ScoringInfo>();
          infos.forEach((i) => {
            if (i.score && i.score > 0) {
              scoresMap.set(i.recordingId, i.score);
            }
            siMap.set(i.recordingId, {
              scoringStatus: i.scoringStatus,
              scoringAttempts: i.scoringAttempts,
              scoringLastAttemptAt: i.scoringLastAttemptAt,
            });
          });
          setRecordingScores(scoresMap);
          setScoringInfo(siMap);
        }
      }
    } catch (error) {
      console.error('Error loading recordings:', error);
      setError('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recording: Recording) => {
    // Prevent deletion of tournament recordings
    if (recording.tournament_id) {
      setError('Tournament recordings cannot be deleted');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setSelectedRecording(recording);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedRecording) return;

    try {
      setIsDeleting(true);
      await recordingService.deleteRecording(selectedRecording.id);
      setRecordings(recordings.filter(r => r.id !== selectedRecording.id));
      setShowDeleteModal(false);
      setSelectedRecording(null);
    } catch (error) {
      console.error('Error deleting recording:', error);
      setError('Failed to delete recording');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditTitle = async (recording: Recording) => {
    setEditingId(recording.id);
    setEditingTitle(recording.title);
  };

  const handleSaveTitle = async (recording: Recording) => {
    try {
      await recordingService.updateRecordingTitle(recording.id, editingTitle);
      setRecordings(recordings.map(r => 
        r.id === recording.id ? { ...r, title: editingTitle } : r
      ));
      setEditingId(null);
    } catch (error) {
      console.error('Error updating title:', error);
      setError('Failed to update recording title');
    }
  };

  const handleDownload = async (recording: Recording) => {
    try {
      setIsDownloading(recording.id);
      setError(null);
      
      if (!recording.song?.audio_url) {
        throw new Error('No backing track available for this recording');
      }
      
      await recordingService.downloadRecordingWithBackingTrack(recording);
    } catch (error) {
      console.error('Error downloading recording:', error);
      setError(error instanceof Error ? error.message : 'Failed to download recording');
    } finally {
      setIsDownloading(null);
    }
  };

  const handleSubmitForScoring = async (recording: Recording) => {
    // Show Jocelyn's modal instead of submitting for scoring
    setRecordingForScoring(recording);
    setShowJocelynModal(true);
  };

  const handleSubmitToLeaderboard = async (recording: Recording) => {
    if (recording.leaderboard_status !== 'unsubmitted') return;

    setSubmittingToLeaderboard(recording.id);
    try {
      const { error: updateError } = await supabase
        .from('recordings')
        .update({ leaderboard_status: 'submitted' })
        .eq('id', recording.id);

      if (updateError) throw updateError;

      // Update local state
      setRecordings(recordings.map(r =>
        r.id === recording.id ? { ...r, leaderboard_status: 'submitted' as const } : r
      ));
    } catch (error) {
      console.error('Error submitting to leaderboard:', error);
      setError('Failed to submit to leaderboard');
    } finally {
      setSubmittingToLeaderboard(null);
    }
  };

  const handlePlay = (recording: Recording) => {
    if (recording.recording_type === 'video') {
      setPlayingVideo(recording);
    } else {
      setPlayingAudio(recording);
    }
    
    if (onPlay) {
      onPlay(recording);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-500">
        {error}
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div className="text-center py-8 text-white/70">
        <div className="mb-4">
          <div className="w-20 h-20 rounded-full bg-kod-purple-700/50 flex items-center justify-center mx-auto">
            <Mic size={28} className="text-kod-accent" />
          </div>
        </div>
        <h3 className="text-xl font-semibold mb-2">No Recordings Yet</h3>
        <p className="text-white/60 max-w-md mx-auto mb-6">Ready to show off your talent? Choose a song from the catalog and start recording your performances.</p>
        <button
          onClick={() => navigate('/audition-songs')}
          className="px-4 py-2 bg-kod-accent hover:bg-kod-accent-dark text-white rounded-lg inline-flex items-center"
        >
          <Mic size={18} className="mr-2" />
          Start Recording
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {recordings.map((recording) => {
        const score = recordingScores.get(recording.id);
        const stage = getStageDisplay(recording);
        const showHint = !!stage.hint && (stage.tone === 'processing' || stage.tone === 'scoring' || stage.tone === 'failed');

        return (
          <div
            key={recording.id}
            className={`relative bg-gradient-to-br from-kod-purple-700/60 to-kod-purple-800/40 rounded-xl overflow-hidden group hover:from-kod-purple-700/70 hover:to-kod-purple-800/50 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] ${
              recording.tournament_id
                ? 'border-2 border-yellow-500/60 hover:border-yellow-500/80'
                : 'border border-kod-purple-600/40'
            }`}
          >
            <div className="flex">
              <div className="relative w-24 h-24 flex-shrink-0">
                <img
                  src={recording.song?.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=96&h=96&fit=crop'}
                  alt={recording.song?.title || 'Song cover'}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handlePlay(recording)}
                    className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                  >
                    <Play size={20} className="text-white" fill="white" />
                  </button>
                </div>
                <div className="absolute bottom-1 left-1">
                  {recording.recording_type === 'video' ? (
                    <div className="bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5">
                      <Video size={12} className="text-white" />
                    </div>
                  ) : (
                    <div className="bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5">
                      <Mic size={12} className="text-white" />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 p-3 min-w-0 flex flex-col justify-between">
                <div>
                  {editingId === recording.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        className="bg-kod-purple-600 border border-kod-purple-500 rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-kod-accent w-full"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveTitle(recording)}
                        className="p-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded flex-shrink-0"
                      >
                        <Check size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 flex-wrap">
                          <h3 className="font-semibold text-white truncate text-sm">{recording.title || recording.song?.title || 'Unknown Song'}</h3>
                          {recording.tournament_id && (
                            <span className="bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 border border-yellow-500/40">
                              {recording.tournament?.title || 'TOURNAMENT'}{recording.tournament_round ? ` - R${recording.tournament_round}` : ''}
                            </span>
                          )}
                          <button
                            onClick={() => handleEditTitle(recording)}
                            className="p-0.5 text-white/40 hover:text-white hover:bg-kod-purple-600 rounded flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit title"
                          >
                            <Pencil size={10} />
                          </button>
                        </div>
                        <p className="text-xs text-white/60 truncate">{recording.song?.title} - {recording.song?.artist || 'Unknown Artist'}</p>
                      </div>
                      {score && score > 0 && (
                        <div className="bg-gradient-to-br from-kod-accent to-orange-500 rounded-md px-2 py-1 shadow flex-shrink-0">
                          <div className="text-lg font-bold text-white leading-none">{score.toFixed(1)}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-xs text-white/40">{formatDate(recording.created_at)}</p>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded-full px-1.5 py-0.5 ${TONE_PILL_CLASSES[stage.tone]}`}>
                      {stage.spinning ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : stage.tone === 'ready' ? (
                        <Check size={10} />
                      ) : stage.tone === 'failed' ? (
                        <AlertCircle size={10} />
                      ) : (
                        <Clock size={10} className="animate-pulse" />
                      )}
                      {stage.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePlay(recording)}
                      className="p-1.5 text-white/60 hover:text-white hover:bg-kod-purple-600 rounded-full transition-colors"
                      title="Play"
                    >
                      <Play size={18} />
                    </button>
                    {getDownloadStatus(recording) === 'processing' ? (
                      <div
                        className="p-1.5 text-amber-300/70"
                        title="Your mixed version is still processing. Check back shortly."
                      >
                        <Clock size={18} className="animate-pulse" />
                      </div>
                    ) : (
                      <button
                        onClick={() => handleDownload(recording)}
                        className="p-1.5 text-white/60 hover:text-white hover:bg-kod-purple-600 rounded-full transition-colors"
                        title="Download"
                        disabled={isDownloading === recording.id}
                      >
                        {isDownloading === recording.id ? (
                          <div className="animate-spin rounded-full h-[18px] w-[18px] border-2 border-t-transparent border-white"></div>
                        ) : (
                          <Download size={18} />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleSubmitForScoring(recording)}
                      className="p-1.5 text-kod-accent hover:text-kod-accent-light hover:bg-kod-purple-600 rounded-full transition-colors"
                      title={stage.tone === 'scoring' ? 'Scoring in progress' : scoredRecordings.has(recording.id) ? 'View Score' : 'Get Score'}
                    >
                      <BarChart2 size={18} />
                    </button>
                    {recording.leaderboard_status === 'unsubmitted' && (
                      <button
                        onClick={() => handleSubmitToLeaderboard(recording)}
                        disabled={submittingToLeaderboard === recording.id}
                        className="p-1.5 text-yellow-400 hover:text-yellow-300 hover:bg-kod-purple-600 rounded-full transition-colors disabled:opacity-50"
                        title="Submit to Leaderboard"
                      >
                        {submittingToLeaderboard === recording.id ? (
                          <div className="animate-spin rounded-full h-[18px] w-[18px] border-2 border-t-transparent border-yellow-400"></div>
                        ) : (
                          <Trophy size={18} />
                        )}
                      </button>
                    )}
                    {recording.leaderboard_status === 'submitted' && (
                      <div className="p-1.5 text-green-400" title="On Leaderboard">
                        <Trophy size={18} />
                      </div>
                    )}
                    {recording.leaderboard_status === 'removed' && (
                      <div className="p-1.5 text-red-400" title="Removed from Leaderboard">
                        <Ban size={18} />
                      </div>
                    )}
                    {!recording.tournament_id && (
                      <button
                        onClick={() => handleDelete(recording)}
                        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-full transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                    {recording.tournament_id && (
                      <div className="p-1.5 text-cyan-400" title="Tournament Entry">
                        <Swords size={18} />
                      </div>
                    )}
                  </div>
                </div>

                {showHint && (
                  <div className={`mt-2 flex items-center gap-1.5 text-[11px] leading-tight rounded-lg px-2 py-1.5 ${stage.tone === 'failed' ? 'bg-red-500/10 text-red-200/90' : stage.tone === 'scoring' ? 'bg-blue-500/10 text-blue-200/90' : 'bg-amber-500/10 text-amber-200/90'}`}>
                    {stage.tone === 'failed' ? (
                      <AlertCircle size={11} className="flex-shrink-0" />
                    ) : (
                      <Loader2 size={11} className="animate-spin flex-shrink-0" />
                    )}
                    <span>{stage.hint}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Fixed Audio Player */}
      {playingAudio && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 w-full max-w-2xl px-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-4 shadow-lg border border-kod-purple-600/30">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">{playingAudio.title}</h3>
                <p className="text-sm text-white/70">
                  {playingAudio.song?.title} • {playingAudio.song?.artist}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPlayingAudio(null)}
                  className="p-2 hover:bg-kod-purple-700 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <AudioWaveform 
              url={playingAudio.recording_url}
              onWaveformInit={(ws) => { wavesurferRef.current = ws; }}
            />
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <PlaybackModal
          recording={playingVideo}
          onClose={() => setPlayingVideo(null)}
          isScored={scoredRecordings.has(playingVideo.id)}
          onSyncCalculated={(offset) => {
            setRecordings(prev => prev.map(r =>
              r.id === playingVideo.id ? { ...r, audio_sync_offset: offset } : r
            ));
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRecording && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-kod-purple-800 p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Delete Recording?</h3>
            <p className="text-white/70 mb-6">
              Are you sure you want to delete this recording? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Jocelyn Score Modal */}
      {showJocelynModal && recordingForScoring && (
        <JocelynScore
          overallScore={0}
          pitchAccuracy={0}
          rhythmAccuracy={0}
          styleScore={0}
          songId={recordingForScoring.song_id}
          songTitle={recordingForScoring.song?.title}
          songArtist={recordingForScoring.song?.artist}
          userRecordingUrl={recordingForScoring.vocals_only_url || recordingForScoring.recording_url}
          originalVocalUrl={recordingForScoring.song?.lead_vocal_url}
          recordingId={recordingForScoring.id}
          onClose={() => setShowJocelynModal(false)}
        />
      )}
    </div>
  );
};